import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Clock, FileText } from "lucide-react";
import Header from "../Header";
import Footer from "../Footer";

const DIAGRAMS = {
    "building-timbot": { src: "/blog/timbot-architecture.png", alt: "TIMBOT request flow architecture diagram", width: 1218, height: 856 },
};

export const POSTS = {
    "search-is-not-evidence": {
        slug: "search-is-not-evidence",
        file: "/blog/search-is-not-evidence.md",
        title: "Building TIMBOT: search is not evidence",
        byline: "TIM // TIMBOTIC LAB — OCT 2026",
        ref: "FIELD REPORT FR-002",
        tags: ["FRESHNESS", "EVIDENCE QUALITY", "TEMPORAL MEMORY", "TEXT-TO-CAD"],
        summary:
            "A search that works is not the same as an answer that's verified. Freshness detection, evidence quality states, memory that keeps history, a constrained text-to-CAD path — and still no swarm.",
    },
    "building-timbot": {
        slug: "building-timbot",
        file: "/blog/building-timbot.md",
        title: "Building TIMBOT: a personal AI assistant with visible boundaries",
        byline: "TIM // TIMBOTIC LAB — SEPT 2026",
        ref: "FIELD REPORT FR-001",
        tags: ["LOCAL INFERENCE", "PERMISSIONS", "LAYA", "MEMORY"],
        summary:
            "Everything around the model turned out to be the interesting part: deterministic routing, a classifier that can recommend but never authorise, memory that stays home, and a web checkbox that means what it says.",
    },
};

const makeComponents = (diagram) => ({
    h1: () => null,
    h2: ({ children }) => (
        <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900 mt-14 mb-4 flex items-baseline gap-3">
            <span className="font-mono text-xs text-cyan-600 tracking-widest">//</span>
            {children}
        </h2>
    ),
    p: ({ children }) => <p className="text-slate-600 leading-relaxed mb-5 text-[15px] sm:text-base">{children}</p>,
    strong: ({ children }) => <strong className="text-slate-900 font-semibold">{children}</strong>,
    blockquote: ({ children }) => (
        <blockquote className="my-6 border-l-2 border-cyan-500 bg-white rounded-r-lg px-5 py-3 font-mono text-[13px] text-slate-600 [&_p]:mb-2 [&_p:last-child]:mb-0">
            {children}
        </blockquote>
    ),
    ul: ({ children }) => <ul className="list-disc pl-6 mb-5 text-slate-600 space-y-1">{children}</ul>,
    a: ({ href, children }) => (
        <a href={href} className="text-cyan-700 underline decoration-cyan-300 hover:decoration-cyan-600" target="_blank" rel="noreferrer">
            {children}
        </a>
    ),
    code: ({ className, children }) => {
        const lang = /language-(\w+)/.exec(className || "")?.[1];
        const text = String(children).replace(/\n$/, "");
        if (lang === "mermaid" && diagram) {
            return (
                <figure className="my-8 lg:-mx-32 xl:-mx-56 rounded-lg bg-white border border-slate-200 p-3 sm:p-5" data-testid="blog-architecture-diagram">
                    <img src={diagram.src} alt={diagram.alt} width={diagram.width} height={diagram.height} loading="lazy" className="w-full h-auto" />
                </figure>
            );
        }
        if (lang) {
            return (
                <pre className="tb-code" data-lang={lang}>
                    <code>{text}</code>
                </pre>
            );
        }
        return <code className="font-mono text-[13px] px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-800 border border-cyan-100">{children}</code>;
    },
    pre: ({ children }) => <>{children}</>,
});

function readingTime(md) {
    return Math.max(1, Math.round(md.split(/\s+/).length / 220));
}

export default function BlogPost({ slug }) {
    const post = POSTS[slug];
    const [md, setMd] = useState("");
    const [state, setState] = useState("loading");

    useEffect(() => {
        window.scrollTo(0, 0);
        if (!post) return setState("missing");
        document.title = `${post.title} | TIMBOTIC`;
        const canonical = document.querySelector('link[rel="canonical"]');
        const prevCanonical = canonical?.getAttribute("href");
        canonical?.setAttribute("href", `https://timbotic.com/blog/${post.slug}`);
        fetch(post.file)
            .then((r) => (r.ok ? r.text() : Promise.reject()))
            .then((t) => {
                setMd(t);
                setState("ready");
            })
            .catch(() => setState("error"));
        return () => {
            document.title = "TIMBOTIC | Inventions, Robotics & Experimental Projects";
            if (prevCanonical) canonical?.setAttribute("href", prevCanonical);
        };
    }, [post]);

    return (
        <div className="App min-h-screen flex flex-col" data-testid="blog-post-page">
            <Header />
            <main className="flex-1 bg-[#f2f4f6]">
                <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
                    <a href="/" data-testid="blog-back-link" className="inline-flex items-center gap-2 font-mono text-xs tracking-widest text-slate-500 hover:text-cyan-700 transition-colors mb-10">
                        <ArrowLeft className="h-3.5 w-3.5" /> RETURN TO LAB
                    </a>

                    {!post || state === "missing" || state === "error" ? (
                        <div className="font-mono text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-4" data-testid="blog-error">
                            [ FIELD REPORT NOT FOUND — FILING CABINET UNRESPONSIVE ]
                        </div>
                    ) : (
                        <>
                            <header className="mb-10 border-b border-subtle pb-8">
                                <div className="font-mono text-xs text-cyan-600 tracking-widest uppercase flex items-center gap-2 mb-4">
                                    <FileText className="h-3.5 w-3.5" /> {post.ref}
                                </div>
                                <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-[1.1] mb-6" data-testid="blog-title">
                                    {post.title}
                                </h1>
                                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-[11px] tracking-wider text-slate-500">
                                    <span data-testid="blog-byline">{post.byline}</span>
                                    {md && (
                                        <span className="inline-flex items-center gap-1.5">
                                            <Clock className="h-3 w-3" /> {readingTime(md)} MIN READ
                                        </span>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-2 mt-4">
                                    {post.tags.map((t) => (
                                        <span key={t} className="font-mono text-[10px] tracking-widest px-2 py-0.5 rounded border border-cyan-500/30 bg-cyan-100/60 text-cyan-700">
                                            {t}
                                        </span>
                                    ))}
                                </div>
                            </header>

                            {state === "loading" ? (
                                <div className="font-mono text-xs text-slate-500 animate-pulse" data-testid="blog-loading">RETRIEVING DOCUMENT FROM ARCHIVE…</div>
                            ) : (
                                <div className="tb-article" data-testid="blog-body">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={makeComponents(DIAGRAMS[slug])}>
                                        {md}
                                    </ReactMarkdown>
                                </div>
                            )}

                            <div className="mt-16 pt-6 border-t border-subtle font-mono text-[11px] tracking-wider text-slate-500 flex flex-wrap justify-between gap-3">
                                <span>END OF FIELD REPORT // {post.ref}</span>
                                <a href="/#timbot" className="hover:text-cyan-700 transition-colors" data-testid="blog-footer-back">
                                    ← BACK TO AGENT_001
                                </a>
                            </div>
                            <nav className="mt-8" aria-label="Other field reports" data-testid="blog-other-posts">
                                <div className="font-mono text-[10px] tracking-widest text-cyan-600 uppercase mb-3">Other field reports</div>
                                <ul className="space-y-2">
                                    {Object.values(POSTS)
                                        .filter((p) => p.slug !== post.slug)
                                        .map((p) => (
                                            <li key={p.slug}>
                                                <a href={`/blog/${p.slug}`} className="group flex items-baseline gap-3 text-sm" data-testid={`blog-other-${p.slug}`}>
                                                    <span className="font-mono text-[10px] text-slate-400 shrink-0">{p.ref.replace("FIELD REPORT ", "")}</span>
                                                    <span className="text-slate-700 group-hover:text-cyan-700 transition-colors">{p.title}</span>
                                                </a>
                                            </li>
                                        ))}
                                </ul>
                            </nav>
                        </>
                    )}
                </article>
            </main>
            <Footer onSafetyTrigger={() => {}} />
        </div>
    );
}
